import React, { useState } from "react";
import { Box, Button, Grid2, Stack, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TableComponent from "components/grid";
import PaginationComponent from "components/ui-components/PaginationComponent";
import { useParams } from "react-router-dom";
import { useGetAllRiskQuery } from "store/slices/apis/riskPackageSlice";
import moment from "moment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import { IoMdSearch } from "react-icons/io";
import ApproveChangeTrackerComponent from "components/action/ApproveChangeTrackerComponent";
import RejectChangeTrackerComponent from "components/action/RejectChangeTrackerComponent";
import EditChangeTracker from "./EditChangeTracker";
import { useGetAllChangeQuery } from "store/slices/apis/changeTrackerPackageSlice";
import { ReactComponent as CalendarIcon } from "assets/PwC_Funct_Icons_Calendar_Outline_Black_RGB.svg"

const columns = [
  {
    name: "Change Tracker ID",
    field: "change_number",
    renderer(params) {
      return (
        <Stack direction="row" spacing={1}>
          {params?.change_number && (
            <Chip
              label={params?.change_number}
              size="small"
              sx={{
                fontSize: "12px",
                "& .MuiChip-label": {
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  fontWeight: 600,
                },
              }}
            />
          )}
        </Stack>
      );
    },
  },
  {
    name: "Category",
    field: "package",
  },
  {
    name: "Sub Category 1",
    field: "sub_package1",
  },
  {
    name: "Workflow Status",
    field: "approved_status",
    renderer(params) {
      return (
        <>
          {params?.approved_status === "Pending_for_Approval"
            ? "Pending for approval"
            : params?.approved_status}
        </>
      );
    },
  },
  {
    name: "Current Status",
    field: "status",
  },
  {
    name: "Created Date",
    field: "createddate",
    renderer(params) {
      return (
        <>
          {params?.createddate
            ? moment(params.createddate).format("DD/MM/YY")
            : "N.A."}
        </>
      );
    },
  },
  {
    name: "Contractor Number",
    field: "contractor_number",
  },
  {
    name: "Consultant Contractor",
    field: "consultant_contractor",
  },
  {
    name: "Change Raised To",
    field: "change_raised_to",
  },
  {
    name: "Change Received On",
    field: "change_received_on",
    renderer(params) {
      return (
        <>
          {params?.change_received_on
            ? moment(params.change_received_on).format("DD/MM/YY")
            : "N.A."}
        </>
      );
    },
  },
  {
    name: "Change Raised By Reference",
    field: "change_raised_by_refrence",
  },
  {
    name: "Change Raised By Date Received",
    field: "change_raised_by_date_received",
    renderer(params) {
      return (
        <>
          {params?.change_raised_by_date_received
            ? moment(params.change_raised_by_date_received).format("DD/MM/YY")
            : "N.A."}
        </>
      );
    },
  },
  // {
  //   name: "Reason For Change",
  //   field: "reason_for_change",
  // },
  {
    name: "Change Raised To Date Of Response",
    field: "change_raised_to_date_of_response",
    renderer(params) {
      return (
        <>
          {params?.change_raised_to_date_of_response
            ? moment(params.change_raised_to_date_of_response).format(
              "DD/MM/YY"
            )
            : "N.A."}
        </>
      );
    },
  },
  {
    name: "Change Raised To Position",
    field: "change_raised_to_position",
  },
  {
    name: "Approved Cost Impact",
    field: "approved_cost_impact",
  },
  {
    name: "Change Raised By Estimate Cost Impact",
    field: "change_raised_by_estimate_cost_impact",
  },
  {
    name: "Change Raised To Estimate Cost Impact",
    field: "change_raised_to_estimate_cost_impact",
  },
  {
    name: "Change Raised By Estimate Time Impact",
    field: "change_raised_by_estimate_time_impact",
  },
  {
    name: "Change Raised To Estimate Time Impact",
    field: "change_raised_to_estimate_time_impact",
  },
  {
    name: "Approved Time Impact",
    field: "approved_time_impact",
  },
  {
    name: "Actual Date Of Closure",
    field: "actual_date_of_closure",
    renderer(params) {
      return (
        <>
          {params?.actual_date_of_closure
            ? moment(params.actual_date_of_closure).format("DD/MM/YY")
            : "N.A."}
        </>
      );
    },
  },
  // {
  //   name: "Description Of Change",
  //   field: "description_of_change",
  // },
  {
    name: "Action",
    field: "action",
    option: {
      view: true,
      delete: false,
      edit: false,
      acceptReject: true,
    },
  },
];

const ChangeTrackerPendingApproval = () => {
  const theme = useTheme();
  const [pageNumber, setPageNumber] = useState(1);
  const [isIssueReject, setIsIssueReject] = useState(false);
  const [rowData, setRowData] = useState(null);
  const [isIssueApprove, setIsIssueApprove] = useState(false);
  const { projectId } = useParams();
  const [approvedStatus, setApprovedStatus] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [filters, setFilters] = useState({
    approvedStatus: "",
    fromDate: "",
    toDate: "",
  });
  const {
    data: allChange,
    refetch,
    isLoading,
  } = useGetAllChangeQuery({
    ProjectId: projectId,
    sortBy: "createddate",
    sortOrder: "desc",
    approved_status: "Pending_for_Approval",
    from: filters?.fromDate,
    to: filters?.toDate,
    page: pageNumber,
    pageSize: 10,
  });
  const [issueData, setEditIssueData] = useState(null);
  const [isViewIssueModal, setIsViewIssueModal] = useState(false);

  const handleActionAcceptIssue = (row) => {
    setIsIssueApprove((prev) => {
      const newState = !prev;
      setRowData(newState ? row : null);
      return newState;
    });
  };

  const handleActionView = (row) => {
    setIsViewIssueModal(!isViewIssueModal);
    if (!issueData) {
      setEditIssueData(row);
    } else {
      setEditIssueData(null);
    }
  };

  const handleActionRejectIssue = (row) => {
    setIsIssueReject((prev) => {
      const newState = !prev;
      setRowData(newState ? row : null);
      return newState;
    });
  };

  const CustomCalendarIcon = (props) => (
      <CalendarIcon {...props} style={{ width: 26, height: 26, fill:"#696969 ", stroke:"#696969", strokeWidth:4 }} />
    );

  return (
    <>
      <Grid2 container spacing={2} sx={{ mt: "20px" }}>
        <Grid2 size={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              sx={{
                flexGrow: 2,
                alignItems: "center",
                "& .MuiStack-root": {
                  paddingTop: "15px",
                },
              }}
            >
              {/* <Autocomplete
                                      options={approvedStatuses}
                                      getOptionLabel={(option) => option?.label}
                                      onChange={(_, newValue) =>
                                        setApprovedStatus(newValue ? newValue?.value : "")
                                      }
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Current Status"
                                          name="approvedStatus"
                                          // slotProps={{ inputLabel: { shrink: true } }}
                                          variant="outlined"
                                          size="small"
                                          sx={{
                                            "& .MuiFilledInput-root": {
                                              backgroundColor: "#fff",
                                            },
                      
                                            "& .MuiInputLabel-root": {
                                              top: "-3px",
                                            },                    
                                            minWidth: "230px",
                                          }}
                                        />
                                      )}
                                      ListboxProps={{
                                        sx: { fontSize: "13px" },
                                      }}
                                      // sx={{ width: "23%", marginTop: "7px !important" }}
                                    /> */}
              <LocalizationProvider dateAdapter={AdapterDayjs} size="small">
                <DemoContainer components={["DatePicker", "DatePicker"]}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    format="DD-MM-YYYY"
                    onChange={(newValue) => setFromDate(newValue)}
                    slots={{
                              openPickerIcon: CustomCalendarIcon,
                            }}
                    slotProps={{
                      textField: {
                        sx: {
                          minWidth: 150,
                          "& .MuiOutlinedInput-input": {
                            padding: "10px",
                          },

                          "& .MuiInputLabel-root": {
                            top: "-6px",
                          },
                        },
                      },
                      field: {
                        clearable: true,
                        onClear: () =>
                          setFilters({
                            fromDate: "",
                            toDate: toDate
                              ? dayjs(toDate).format("YYYY-MM-DD")
                              : "",
                            approvedStatus,
                          }),
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker", "DatePicker"]}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    format="DD-MM-YYYY"
                    clearable={true}
                    onChange={(newValue) => setToDate(newValue)}
                    slots={{
                              openPickerIcon: CustomCalendarIcon,
                            }}
                    slotProps={{
                      textField: {
                        sx: {
                          minWidth: 150,
                          "& .MuiOutlinedInput-input": {
                            padding: "10px",
                          },

                          "& .MuiInputLabel-root": {
                            top: "-6px",
                          },
                        },
                      },
                      field: {
                        clearable: true,
                        onClear: () =>
                          setFilters({
                            fromDate: fromDate
                              ? dayjs(fromDate).format("YYYY-MM-DD")
                              : "",
                            toDate: "",
                            approvedStatus,
                          }),
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
              <Button
                loading={isLoading}
                onClick={() => {
                  setFilters({
                    fromDate: fromDate
                      ? dayjs(fromDate).format("YYYY-MM-DD")
                      : "",
                    toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : "",
                    approvedStatus,
                  });
                }}
                size="large"
                startIcon={<IoMdSearch />}
                variant="outlined"
                sx={{
                  color: `${theme?.customSettings?.themeColor}`,
                  border: `1px solid ${theme?.customSettings?.themeColor}`,
                  background: "none",
                  textTransform: "inherit",
                  padding: "6px 20px",
                  marginTop: "12px !important",
                }}
              >
                Search
              </Button>
              <Button
                loading={isLoading}
                onClick={() => {
                  setFromDate(null);
                  setToDate(null);
                  const clearedValues = Object.fromEntries(
                    Object.keys(filters).map((key) => [key, ""])
                  );
                  setFilters(clearedValues);
                }}
                size="large"
                variant="outlined"
                sx={{
                  color: `${theme?.customSettings?.themeColor}`,
                  border: `1px solid ${theme?.customSettings?.themeColor}`,
                  background: "none",
                  textTransform: "inherit",
                  padding: "6px 15px",
                  marginTop: "12px !important",
                }}
              >
                Reset
              </Button>
            </Stack>
          </Box>
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <TableComponent
            columns={columns}
            data={allChange?.data?.filter((risk) => risk?.active === true)}
            // actionDelete={handleActionDelete}
            // actionEdit={handleActionEdit}
            actionView={handleActionView}
            actionAccept={handleActionAcceptIssue}
            actionReject={handleActionRejectIssue}
            isLoading={isLoading}
          />
          {!isLoading && (
            <Box sx={{ display: "flex", justifyContent: "end" }}>
              <PaginationComponent
                pageNumber={allChange?.data?.page || 1}
                totalPages={
                  Math.ceil(
                    allChange?.data?.totalRecords / allChange?.data?.pageSize
                  ) || 1
                }
                onPageChange={setPageNumber}
              />
            </Box>
          )}
        </Grid2>
      </Grid2>
      {isViewIssueModal && (
        <EditChangeTracker
          cancelIssue={handleActionView}
          open={isViewIssueModal}
          data={issueData}
          isViewIssueModal
        />
      )}
      <ApproveChangeTrackerComponent
        open={isIssueApprove}
        cancel={handleActionAcceptIssue}
        rowData={rowData}
        keys={"change"}
        refetch={refetch}
      />
      <RejectChangeTrackerComponent
        open={isIssueReject}
        cancel={handleActionRejectIssue}
        rowData={rowData}
        keys={"change"}
        refetch={refetch}
      />
    </>
  );
};

export default ChangeTrackerPendingApproval;
